/* eslint-disable react-refresh/only-export-components */
import React, { useSyncExternalStore } from "react";
import {
  CheckCircle2,
  CircleAlert,
  CircleX,
  Info,
  type LucideIcon,
  X,
} from "lucide-react";

import { useTranslation } from "@/lib/hooks/use-translation";
import { APP_SHELL } from "@/lib/constants/workspace-layout";
import { cn } from "@/lib/utils";

export type AppNotificationKind = "success" | "error" | "warning" | "info";

export const APP_NOTIFICATION_DURATION_MS = 10000;

export interface AppNotificationOptions {
  description?: string;
  durationMs?: number;
  id?: string | number;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface AppNotificationToastProps extends AppNotificationOptions {
  toastId: string | number;
  kind: AppNotificationKind;
  title: string;
}

type AppNotificationItem = Required<Pick<AppNotificationToastProps, "toastId" | "kind" | "title">> &
  AppNotificationOptions;

let notificationItems: AppNotificationItem[] = [];
let notificationSequence = 0;
const notificationListeners = new Set<() => void>();
const notificationTimers = new Map<string | number, ReturnType<typeof setTimeout>>();

function emitNotifications(): void {
  notificationListeners.forEach((listener) => listener());
}

function notificationContentKey(kind: AppNotificationKind, title: string, description?: string): string {
  return `${kind}:${title}:${description ?? ""}`;
}

function dismissNotification(id?: string | number): void {
  if (id === undefined) {
    notificationTimers.forEach((timer) => clearTimeout(timer));
    notificationTimers.clear();
    notificationItems = [];
  } else {
    const timer = notificationTimers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      notificationTimers.delete(id);
    }
    notificationItems = notificationItems.filter((item) => item.toastId !== id);
  }
  emitNotifications();
}

function dismissMatchingNotifications(
  kind: AppNotificationKind,
  title: string,
  options: AppNotificationOptions,
): void {
  const contentKey = notificationContentKey(kind, title, options.description);
  const staleIds = notificationItems
    .filter(
      (item) =>
        item.toastId === options.id ||
        notificationContentKey(item.kind, item.title, item.description) === contentKey,
    )
    .map((item) => item.toastId);
  staleIds.forEach((toastId) => dismissNotification(toastId));
}

const KIND_STYLES: Record<
  AppNotificationKind,
  { Icon: LucideIcon; accent: string; rail: string; progress: string; track: string }
> = {
  success: {
    Icon: CheckCircle2,
    accent: "text-success",
    rail: "bg-success",
    progress: "bg-success",
    track: "bg-success/15",
  },
  error: {
    Icon: CircleX,
    accent: "text-destructive",
    rail: "bg-destructive",
    progress: "bg-destructive",
    track: "bg-destructive/15",
  },
  warning: {
    Icon: CircleAlert,
    accent: "text-warning",
    rail: "bg-warning",
    progress: "bg-warning",
    track: "bg-warning/15",
  },
  info: {
    Icon: Info,
    accent: "text-primary",
    rail: "bg-primary",
    progress: "bg-primary",
    track: "bg-primary/15",
  },
};

export const AppNotificationToast: React.FC<AppNotificationToastProps> = ({
  toastId,
  kind,
  title,
  description,
  durationMs = APP_NOTIFICATION_DURATION_MS,
  dismissible = true,
  action,
}) => {
  const { t } = useTranslation();
  const { Icon, accent, rail, progress, track } = KIND_STYLES[kind];
  const assertive = kind === "error" || kind === "warning";
  const showProgress = durationMs > 0;

  return (
    <div
      role={assertive ? "alert" : "status"}
      aria-live={assertive ? "assertive" : "polite"}
      data-notification-kind={kind}
      data-duration-ms={durationMs}
      className="surface-card pointer-events-auto relative w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl animate-app-notification-enter"
    >
      <div className={cn("absolute inset-y-0 left-0 w-1", rail)} aria-hidden />
      <div className="flex items-start gap-2 py-2.5 pl-3 pr-2">
        <span
          className={cn(
            "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60 ring-1 ring-border/60",
            accent,
          )}
          aria-hidden
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="break-words text-xs font-semibold leading-snug tracking-tight text-foreground">{title}</p>
          {description ? (
            <p className="mt-1 break-words text-xs leading-snug text-muted-foreground">{description}</p>
          ) : null}
          {action ? (
            <button
              type="button"
              className={cn("mt-2 text-xs font-semibold hover:underline", accent)}
              onClick={() => {
                action.onClick();
                dismissNotification(toastId);
              }}
            >
              {action.label}
            </button>
          ) : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t("common.dismissNotification")}
            onClick={() => dismissNotification(toastId)}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {showProgress ? (
        <div className={cn("h-0.5 w-full", track)}>
          <div
            className={cn("h-full w-full origin-left", progress)}
            style={{
              animation: `app-notification-progress ${durationMs}ms linear forwards`,
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

function showNotification(
  kind: AppNotificationKind,
  title: string,
  options: AppNotificationOptions = {},
): string | number {
  dismissMatchingNotifications(kind, title, options);
  const toastId = options.id ?? `app-notification-${Date.now()}-${notificationSequence++}`;
  dismissNotification(toastId);
  const durationMs = options.durationMs ?? APP_NOTIFICATION_DURATION_MS;
  notificationItems = [...notificationItems, { toastId, kind, title, ...options, durationMs }].slice(-5);
  if (durationMs > 0) {
    notificationTimers.set(
      toastId,
      setTimeout(() => dismissNotification(toastId), durationMs),
    );
  }
  emitNotifications();
  return toastId;
}

export const appNotification = {
  show: showNotification,
  success: (title: string, options?: AppNotificationOptions) => showNotification("success", title, options),
  error: (title: string, options?: AppNotificationOptions) =>
    showNotification("error", title, { ...options, durationMs: options?.durationMs ?? 0 }),
  warning: (title: string, options?: AppNotificationOptions) =>
    showNotification("warning", title, { ...options, durationMs: options?.durationMs ?? 0 }),
  info: (title: string, options?: AppNotificationOptions) => showNotification("info", title, options),
  dismiss: dismissNotification,
};

function subscribeNotifications(listener: () => void): () => void {
  notificationListeners.add(listener);
  return () => notificationListeners.delete(listener);
}

function getNotificationSnapshot(): AppNotificationItem[] {
  return notificationItems;
}

export const AppNotificationHost: React.FC = () => {
  const items = useSyncExternalStore(subscribeNotifications, getNotificationSnapshot, getNotificationSnapshot);

  return (
    <div
      className={cn(
        "pointer-events-none fixed right-3 z-[60] flex max-w-[calc(100vw-1.5rem)] flex-col items-end gap-2 sm:right-4",
        APP_SHELL.notificationHostOffset,
      )}
      data-testid="app-notification-host"
    >
      {items.map((item) => (
        <AppNotificationToast key={item.toastId} {...item} />
      ))}
    </div>
  );
};
